
import RequestPromises from "@/mixins/requestPromises";
import { DataModules } from "@/store/modules/data/index";
import { Component, Prop, Provide, Watch } from "vue-property-decorator";
import {
  IOAuthProvider,
  IOAuthProviderMethods,
  GetOAuthApplicationArgs
} from "@/models/providers/oauth";
import { mapState } from "vuex";
import { IState } from "@/store";
import { IOAuthAuthorization, IOAuthToken } from "@/models/oauth";
import { OAuthConnection } from "@/data/enums/oauth";
import { QUERY_PARAMS } from "@/data/constants";
import { IProvider } from "@/models/providers";
import { CreateElement } from "vue";

@Component({
  computed: {
    ...mapState({
      oauthScope(state: IState, getters) {
        return getters[`data/${DataModules.OAUTH}/scope`](
          this.oauthApplicationCode
        );
      },
      authorization(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.OAUTH_AUTHORIZATIONS
        });
      },
      token(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.OAUTH_TOKENS
        });
      }
    })
  }
})
export default class OAuthProvider extends RequestPromises
  implements IProvider {
  // Props
  @Prop({ required: true })
  public readonly oauthApplicationCode!: string;

  @Prop({ default: null })
  public readonly oauthTokenUuid!: string;

  @Prop({ default: () => [] })
  public readonly queryParams!: { key: string; value: string }[];

  @Prop({ default: false })
  public readonly disabled!: boolean;

  @Prop({ default: false })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  // Provide
  @Provide("oauthProvider")
  public providerData(): IOAuthProvider {
    return {
      selectedToken: this.selectedToken,
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      hasRequestError: this.hasRequestError,
      isProcessingNewAuth: this.isProcessingNewAuth,
      isDisabled: this.disabled,
      authorization: this.authorization,
      token: this.token
    };
  }

  @Provide("oauthProviderMethods")
  public get providerMethods(): IOAuthProviderMethods {
    return {
      getOAuthApplication: this.getOAuthApplication,
      getOAuthAuthorization: this.getOAuthAuthorization,
      initNewAuthAuthorization: this.initNewAuthAuthorization,
      deleteOAuthToken: this.deleteOAuthToken,
      updateOAuthToken: this.updateOAuthToken,
      openEditTokenModal: this.openEditTokenModal,
      setSelectedToken: this.setSelectedToken,
      reloadData: this.reloadData
    };
  }

  // Data
  public readonly authorization!: IOAuthAuthorization;
  public readonly token!: IOAuthToken;
  public oauthScope!: string;
  public isLoading = false;
  public isReloading = false;
  public hasRequestError = false;
  public isProcessingNewAuth = false;
  public selectedToken?: IOAuthToken | null = null;
  public binList: Function[] = [];

  // Lifecycle methods
  public created() {
    this.loadData();
  }

  public beforeDestroy() {
    this.binList.forEach(func => func());
  }

  // Getters
  public get isEdit() {
    return !this.$_.isEmpty(this.oauthTokenUuid);
  }

  public get hasAuthError() {
    return (this.authorization
      ? !this.authorization.success
      : false) as boolean;
  }

  // Watchers

  @Watch("hasAuthError", { immediate: true })
  public onAuthError(hasError) {
    if (hasError) this.handleError();
  }

  @Watch("selectedToken")
  public onSelectedTokenChange() {
    this.$emit("select-token", this.selectedToken);
  }

  // Methods
  public async loadData() {
    try {
      this.isLoading = true;
      if (this.isEdit) {
        await this.getOAuthToken({ uuid: this.oauthTokenUuid });
        this.selectedToken = this.token;
        await this.getOAuthApplication({ id: this.token.application_id });
      } else {
        // New
        await this.getOAuthApplication({ code: this.oauthApplicationCode });
        this.checkAndSetTokenFromURL();
      }
    } catch (error) {
      this.hasRequestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    return this.loadData();
  }

  public async checkAndSetTokenFromURL() {
    const query = this.$route.query;
    const authorizationUuid = query[QUERY_PARAMS.AUTHORIZATION_UUID];
    if (query["success"] === OAuthConnection.ABORTED) this.handleAbort();
    if (query["success"] === OAuthConnection.SUCCESS) this.handleSuccess();
    this.removeQueryParams(["success"]);

    if (authorizationUuid) {
      // Remove query params
      this.removeQueryParams([QUERY_PARAMS.AUTHORIZATION_UUID]);
      await this.getOAuthAuthorization({
        uuid: authorizationUuid
      });

      this.selectedToken = this.authorization?.access_token;
    }
  }

  public handleAbort() {
    this.$buefy.snackbar.open({
      message: this.$t("_sentence.oauth.authorisation_aborted") as string,
      actionText: this.$t("_action.dismiss") as string,
      type: "is-caution"
    });
  }

  public handleError() {
    const message = this.$_.get(this.authorization, "error_message");
    if (!message) return;
    this.$buefy.snackbar.open({
      message,
      actionText: this.$t("_action.dismiss") as string,
      type: "is-danger",
      indefinite: true
    });
  }

  public handleSuccess() {
    this.$buefy.snackbar.open({
      message: this.$t("_sentence.oauth.account_connected") as string,
      actionText: this.$t("_action.dismiss") as string,
      type: "is-success"
    });
  }

  public getOAuthApplication({ id, code }: GetOAuthApplicationArgs) {
    return this.$store
      .dispatch(`data/${DataModules.OAUTH}/get`, {
        id,
        code,
        scope: this.oauthScope,
        vm: this
      })
      .finally(() => {
        this.binList.push(() => this.binOAuthApplication());
      });
  }

  public getOAuthAuthorization({ uuid }) {
    return this.$store
      .dispatch(`data/${DataModules.OAUTH_AUTHORIZATIONS}/get`, {
        uuid,
        vm: this
      })
      .finally(() => {
        this.binList.push(() => this.binOAuthAuthorization());
      });
  }

  public getOAuthToken({ uuid }) {
    return this.$store
      .dispatch(`data/${DataModules.OAUTH_TOKENS}/get`, {
        uuid,
        vm: this
      })
      .finally(() => {
        this.binList.push(() => this.binOAuthToken());
      });
  }

  public deleteOAuthToken({ uuid }) {
    return this.$store.dispatch(`data/${DataModules.OAUTH_TOKENS}/remove`, {
      uuid,
      vm: this
    });
  }

  public updateOAuthToken({ uuid, data }) {
    return this.$store.dispatch(`data/${DataModules.OAUTH_TOKENS}/update`, {
      uuid,
      data
    });
  }

  public openEditTokenModal(payload) {
    return this.$store.dispatch(
      `data/${DataModules.OAUTH_TOKENS}/openEditModal`,
      { ...payload, parent: this }
    );
  }

  public setSelectedToken(token: IOAuthToken) {
    this.selectedToken = token;
  }

  public generateReturnRoute({ withAbort = false }) {
    // Alway add success=-1 in case they click 'Back' from browser to indicate it's aborted
    const params = [...this.queryParams];
    if (withAbort)
      params.push({
        key: "success",
        value: OAuthConnection.ABORTED
      });

    // ${location.href}/new will open add gateway modal
    return this.$router.resolve({
      name: "admin__settingsPaymentProvidersOpenModal",
      query: params.reduce((res, i) => {
        res[i.key] = i.value;
        return res;
      }, {})
    });
  }

  public removeQueryParams(params) {
    this.$router
      .replace({
        query: this.$_.omit(this.$route.query, params)
      })
      .catch(() => {
        /** Mask error */
      });
  }

  public redirectToExternal({ returnRoute, authorization_url }) {
    window.history.pushState(
      "",
      "",
      `${location.origin}${returnRoute.route.fullPath}`
    );
    location.href = authorization_url;
  }

  public initNewAuthAuthorization({ sandbox }) {
    /**
     * User is redirected so we don't need to set isProcessingNewAuth = false
     */
    this.isProcessingNewAuth = true;
    const returnRoute = this.generateReturnRoute({ withAbort: false });

    return this.$store
      .dispatch(
        `data/${DataModules.OAUTH_AUTHORIZATIONS}/initNewAuthAuthorization`,
        {
          data: {
            sandbox,
            application_code: this.oauthApplicationCode,
            return_url: `${location.origin}${returnRoute.route.fullPath}`
          }
        }
      )
      .then(response => {
        if (response?.authorization_url)
          this.redirectToExternal({
            returnRoute: this.generateReturnRoute({ withAbort: true }),
            authorization_url: response?.authorization_url
          });
      })
      .catch(error => {
        this.isProcessingNewAuth = false;
        this.$store.dispatch("api/handleError", error);
      });
  }

  public binOAuthApplication() {
    this.$store.dispatch(`data/binSingle`, {
      storeModule: DataModules.OAUTH,
      scope: this.oauthScope,
      vm: this
    });
  }

  public binOAuthAuthorization() {
    this.$store.dispatch(`data/binSingle`, {
      storeModule: DataModules.OAUTH_AUTHORIZATIONS,
      vm: this
    });
  }

  public binOAuthToken() {
    this.$store.dispatch(`data/binSingle`, {
      storeModule: DataModules.OAUTH_TOKENS,
      vm: this
    });
  }

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });
    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isLoading && this.hasRequestError },
        on: { click: () => this.reloadData() }
      });

    // Renderless, no HTML is rendered for this component
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $oauthData: this.providerData(),
        $oauthMethods: this.providerMethods
      })
    );
  }
}
